import React, { useEffect, useState } from 'react';
import { Box, FormControlLabel, TextField, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { FormData, NewSchool, School } from './types/school';
import { fetchSchools } from '../../redux/slices/School';
import school from '../../apis/school';
import { StyledButton } from './styled';
import { SnackbarSeverityType } from '../../enums';
import StyledCheckbox from '../common/styled/Checkbox';

const EditSchoolForm = ({
  handleModalClose,
  setSnackbar,
  schoolId
}: {
  handleModalClose: (setFormData: any) => void;
  setSnackbar: React.Dispatch<
    React.SetStateAction<{
      show: boolean;
      severity: SnackbarSeverityType;
      message: string;
    }>
  >;
  schoolId: string;
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { schools } = useSelector((state: RootState) => state.school);

  const singleSchool: School | undefined = schools.find(
    (school: School) => school.id === schoolId
  );

  const initialFormData: FormData = {
    shortCode: singleSchool?.shortCode ?? '',
    displayCode: singleSchool?.displayCode ?? '',
    isSameAsShortCode: false,
    structuredPostTypes: [],
    cloneHashtags: false,
    cloneStructuredPost: false,
    cloneStructuredPostSchool: '',
    cloneHashtagSchool: ''
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (formData.isSameAsShortCode) {
      setFormData((prev) => ({ ...prev, displayCode: prev.shortCode }));
    }
  }, [formData.shortCode, formData.isSameAsShortCode]);

  const handleShortCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    // Allow only alphabets and hyphens
    const sanitizedValue = value.replace(/[^A-Z-]/g, '');
    setFormData((prev) => ({ ...prev, shortCode: sanitizedValue }));
  };

  const handleOnChange = (
    e: React.ChangeEvent<{
      name?: string;
      value?: unknown;
      checked?: boolean;
      type?: string;
    }>
  ) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    const newSchool: Partial<NewSchool> = {
      id: schoolId,
      shortCode: formData.shortCode,
      displayCode: formData.displayCode
    };

    try {
      const response = await school.saveSchool(newSchool as NewSchool);
      if (response) {
        setSnackbar({
          show: true,
          severity: SnackbarSeverityType.success,
          message: 'School updated successfully!'
        });
        setLoading(false);
        handleCancel();
        dispatch(fetchSchools());
      }
    } catch (error: any) {
      setSnackbar({
        show: true,
        severity: SnackbarSeverityType.error,
        message:
          error.errors[0].message ??
          'Failed to update school. Please try again.'
      });
      setLoading(false);
      handleCancel();
    }
  };

  const handleCancel = () => {
    handleModalClose(setFormData(initialFormData));
  };

  return (
    <form
      onSubmit={(e) => {
        handleSave(e);
      }}
    >
      <Typography variant='h5' component='h2' style={{ fontWeight: 'bold' }}>
        Edit School
      </Typography>

      <TextField
        fullWidth
        margin='normal'
        label="School's Short Code"
        value={formData.shortCode}
        required
        disabled
        name='shortCode'
        onChange={handleShortCodeChange}
        helperText='Only alphabets and hyphens are allowed.'
        variant='outlined'
      />

      <TextField
        fullWidth
        margin='normal'
        label="School's Display Code"
        value={formData.displayCode}
        required
        name='displayCode'
        onChange={handleOnChange}
        disabled={formData.isSameAsShortCode}
        variant='outlined'
      />
      <FormControlLabel
        control={
          <StyledCheckbox
            name='isSameAsShortCode'
            checked={formData.isSameAsShortCode}
            onChange={handleOnChange}
          />
        }
        label='Same as above'
      />

      <Box
        style={{
          marginTop: 4,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 4
        }}
      >
        <StyledButton
          onClick={handleCancel}
          variant='contained'
          disabled={loading}
        >
          Cancel
        </StyledButton>
        <StyledButton variant='contained' type='submit' disabled={loading}>
          Save School
        </StyledButton>
      </Box>
    </form>
  );
};

export default EditSchoolForm;
